import React, { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";

import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import {
    getIceVehicleOptionsTemplate,
    getIceVehicleSpecs,
    getVehicleYearOptions,
    getVehicleManufacturerOptions,
    getVehicleModelOptions,
} from "utils/transportation";
import { useStateSelector } from "utils/field";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import Actions from "../Actions";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

// Validations
const commuteDistanceValidation = getValidation("commuteDistance");
const distanceValidation = getValidation("distance");
const engineSizeValidation = getValidation("engineSize");
const emissionsValidation = getValidation("emissionsRate");
const combinedFuelEconomyValidation = getValidation("fuelEconomy");
const fuelConsumptionValidation = getValidation("fuelConsumption");

// Helper Functions (within scope of function)
function resetValues(change, accessor) {
    change(`${accessor}.vehicleClass`, "");
    change(`${accessor}.engineSize`, "");
    change(`${accessor}.cylinders`, "");
    change(`${accessor}.transmission`, "");
    change(`${accessor}.emissions`, "");
    change(`${accessor}.emissionRating`, "");
    change(`${accessor}.smogRating`, "");
    change(`${accessor}.fuelType`, "");
    change(`${accessor}.combinedFuelEconomy`, "");

    change(`${accessor}.annualEmissions`, 0);
    change(`${accessor}.annualFuelConsumption`, 0);
    change(`${accessor}.annualFuelCost`, 0);
}

function modelToolTip(model) {
    switch (true) {
        case model.includes("(Low)"):
            return "Performance is calculated annually based on fuel economy (L/100km) and vehicle class. A 'Low' rating means the vehicle was in the 25th percentile for performance for the year based on these factors.";
        case model.includes("(Mid)"):
            return "Performance is calculated annually based on fuel economy (L/100km) and vehicle class. A 'Mid' rating means the vehicle was in the 50th percentile for performance for the year based on these factors.";
        case model.includes("(High)"):
            return "Performance is calculated annually based on fuel economy (L/100km) and vehicle class. A 'High' rating means the vehicle was in the 75th percentile for performance for the year based on these factors.";
        default:
            return "Performance is calculated annually based on fuel economy (L/100km) and vehicle class.";
    }
}

export default ({ accessor, regularGasPrice, premiumGasPrice, dieselPrice, change, formName }) => {
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const contentOpen = useStateSelector(`model.contentOpen`) || false;

    // Unit Assignment
    const commuteDistanceUnits = useStateSelector(`${accessor}.commuteDistance_u`);
    const distanceUnits = useStateSelector(`${accessor}.distance_u`);
    const engineSizeUnits = useStateSelector(`${accessor}.engineSize_u`);
    const fuelEconomyUnits = useStateSelector(`${accessor}.fuelEconomy_u`);
    const priceUnits = useStateSelector(`${accessor}.price_u`);

    // Form Value Assignment
    const commuteDistance = useStateSelector(`${accessor}.commuteDistance`) || 0;
    const sysNum = useStateSelector(`${accessor}.systemNumber`) || 1;
    const vehicleYear = useStateSelector(`${accessor}.vehicleYear`) || "";
    const manufacturer = useStateSelector(`${accessor}.manufacturer`);
    const model = useStateSelector(`${accessor}.model`) || "";
    const vehicleClass = useStateSelector(`${accessor}.vehicleClass`) || "";
    const engineSize = useStateSelector(`${accessor}.engineSize`) || "";
    const cylinders = useStateSelector(`${accessor}.cylinders`) || "";
    const transmission = useStateSelector(`${accessor}.transmission`) || "";
    const emissions = useStateSelector(`${accessor}.emissions`) || "";
    const emissionRating = useStateSelector(`${accessor}.emissionRating`) || "";
    const smogRating = useStateSelector(`${accessor}.smogRating`) || "";
    const fuelType = useStateSelector(`${accessor}.fuelType`) || "";
    const combinedFuelEconomy = useStateSelector(`${accessor}.combinedFuelEconomy`) || "";

    // Option Retrieval
    const vehicleMap = getIceVehicleOptionsTemplate(); //get all ice-vehicle data
    const yearOpts = getVehicleYearOptions(vehicleMap);
    const manufacturerOpts = getVehicleManufacturerOptions(vehicleYear, vehicleMap);
    const modelOpts = getVehicleModelOptions(vehicleYear, manufacturer, vehicleMap);

    const {
        //returned values
        fuelTypeInfo,
        vehicleClassInfo,
        transmissionInfo,
        annualDistanceDriven,
        annualEmissions,
        annualFuelConsumption,
        annualFuelCost,
    } = getIceVehicleSpecs(
        commuteDistance,
        {
            //vehicleSpecs
            vehicleYear,
            manufacturer,
            model,
            vehicleClass,
            engineSize,
            cylinders,
            transmission,
            emissions,
            emissionRating,
            smogRating,
            fuelType,
            combinedFuelEconomy,
        },
        {
            //fuelPrices
            regularGasPrice,
            premiumGasPrice,
            dieselPrice,
        }
    );

    const [reRenderKey, setReRenderKey] = useState(0);

    useEffect(() => {
        setReRenderKey((prev) => prev + 1); // force re-render if year, model, or manufacturer change
    }, [vehicleYear, model, manufacturer]);

    // Change Event Handlers
    const handleYearChange = () => {
        change(`${accessor}.manufacturer`, ""); // Reset manufacturer when year changes
        change(`${accessor}.model`, ""); // Reset model when year changes
        resetValues(change, accessor);
    };

    const handleManufacturerChange = (selectedManufacturer) => {
        change(`${accessor}.manufacturer`, selectedManufacturer);
        change(`${accessor}.model`, ""); // Reset model when manufacturer changes
        resetValues(change, accessor);
    };

    const handleModelChange = (selectedModel) => {
        change(`${accessor}.model`, selectedModel);

        const vehicleData = vehicleMap?.[vehicleYear]?.[manufacturer]?.[selectedModel] || {};

        if (isEmpty(vehicleData)) {
            console.error("Vehicle data is not found"); // Handle the case where vehicleData is empty
        } else {
            change(`${accessor}.vehicleClass`, vehicleData.vehicleClass);
            change(`${accessor}.engineSize`, vehicleData.generalSpecifications.engineSize);
            change(`${accessor}.cylinders`, vehicleData.generalSpecifications.cylinders);
            change(`${accessor}.transmission`, vehicleData.generalSpecifications.transmission);
            change(`${accessor}.emissions`, vehicleData.generalSpecifications.emissions);
            change(`${accessor}.emissionRating`, vehicleData.generalSpecifications.emissionRating);
            change(`${accessor}.smogRating`, vehicleData.generalSpecifications.smogRating);
            change(`${accessor}.fuelType`, vehicleData.gasSpecifications.fuelType);
            change(`${accessor}.combinedFuelEconomy`, vehicleData.gasSpecifications.combinedFuelEconomy);
        }
    };

    // Label Creation
    const createLabel = () => {
        if (vehicleYear && manufacturer && !model) {
            return `${vehicleYear} ${manufacturer}`;
        } else if (vehicleYear && manufacturer && model) {
            return `${vehicleYear} ${manufacturer} ${model}`;
        } else {
            return `Internal Combustion Engine | Vehicle ${sysNum}`;
        }
    };

    return (
        <Accordion
            className={sharedClasses.accordion}
            heading={<span className={classes.accordionTitle}>{createLabel()}</span>}
            large
            hoverActions={<Actions accessor={accessor} change={change} formName={formName} />}
            forceOpen={contentOpen}
        >
            <div key={reRenderKey}>
                <h4>Driving Behavior</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.commuteDistance`}
                        label="Round-trip Daily Commute Distance"
                        placeholder={"0.0"}
                        validate={commuteDistanceValidation}
                        decimals={getDecimalPlaces("commuteDistance")}
                        change={change}
                        units={{
                            base: getBaseUnits("commuteDistance", modelUnits),
                            options: getUnitOptions("distance"),
                            selected: commuteDistanceUnits,
                            unitType: "distance",
                            accessor: `${accessor}.commuteDistance_u`,
                        }}
                        setValue={Math.min(Math.max(commuteDistance, 0), 1000)}
                    />
                </InputRow>

                <h4>General Specifications</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.vehicleYear`}
                        options={yearOpts}
                        label="Year"
                        placeholder="Choose Year"
                        onChange={handleYearChange}
                        setValue={vehicleYear}
                    />
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.manufacturer`}
                        options={manufacturerOpts}
                        label="Manufacturer"
                        placeholder="Choose Manufacturer"
                        info={
                            '"Generic" provides pre-selected vehicle models that fall into the 25th (Low), 50th (Mid), and 75th (High) percentiles for their year, based on fuel economy (L/100km) and vehicle class.'
                        }
                        onChange={handleManufacturerChange}
                        disabled={!vehicleYear}
                        setValue={manufacturer}
                    />
                    <Field
                        component={Select}
                        search={true}
                        name={`${accessor}.model`}
                        options={modelOpts}
                        label="Model"
                        placeholder="Choose Model"
                        info={modelToolTip(model)}
                        onChange={handleModelChange}
                        disabled={!vehicleYear || !manufacturer}
                        setValue={model}
                    />
                </InputRow>

                <h4>Vehicle Specifications</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.engineSize`}
                        label="Engine Size"
                        validate={engineSizeValidation}
                        decimals={getDecimalPlaces("engineSize")}
                        change={change}
                        info="Total displacement of all cylinders."
                        units={{
                            base: getBaseUnits("engineSize", modelUnits),
                            options: getUnitOptions("engine"),
                            selected: engineSizeUnits,
                            unitType: "engine",
                            accessor: `${accessor}.engineSize_u`,
                        }}
                        setValue={engineSize}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.cylinders`}
                        label="Cylinders"
                        setValue={cylinders}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.transmission`}
                        label="Transmission"
                        info={transmissionInfo}
                        setValue={transmission}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.emissions`}
                        label="CO₂ Emissions Rate"
                        validate={emissionsValidation}
                        decimals={getDecimalPlaces("emissionsRate")}
                        change={change}
                        units={{
                            base: getBaseUnits("emissionsRate", modelUnits),
                            options: getUnitOptions("emissionsRate"),
                            unitType: "emissionsRate",
                            accessor: `${accessor}.emissionsRate_u`,
                        }}
                        setValue={emissions}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.emissionRating`}
                        label="CO₂ Rating"
                        info="Rated on a scale from 1 (worst) to 10 (best)"
                        setValue={emissionRating === null ? "N/A" : emissionRating}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.smogRating`}
                        label="Smog Rating"
                        info="Rated on a scale from 1 (worst) to 10 (best)"
                        setValue={smogRating === null ? "N/A" : smogRating}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.vehicleClass`}
                        label="Vehicle Class"
                        info={vehicleClassInfo}
                        setValue={vehicleClass}
                        disabled={true}
                    />
                    <Field
                        component={Input}
                        name={`${accessor}.fuelType`}
                        label="Fuel Type"
                        setValue={fuelTypeInfo}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.combinedFuelEconomy`}
                        label="Combined Fuel Economy"
                        validate={combinedFuelEconomyValidation}
                        decimals={getDecimalPlaces("fuelEconomy")}
                        info="Value provided by NRCan reflects 55% city driving and 45% highway driving."
                        change={change}
                        units={{
                            base: getBaseUnits("fuelEconomy", modelUnits),
                            options: getUnitOptions("fuelEconomy"),
                            selected: fuelEconomyUnits,
                            unitType: "fuelEconomy",
                            accessor: `${accessor}.fuelEconomy_u`,
                        }}
                        setValue={combinedFuelEconomy}
                        disabled={true}
                    />
                </InputRow>

                <h4>Results Preview</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualDistanceDriven`}
                        label="Annual Distance Driven"
                        validate={distanceValidation}
                        decimals={getDecimalPlaces("distance")}
                        change={change}
                        units={{
                            base: getBaseUnits("distance", modelUnits),
                            options: getUnitOptions("distance"),
                            selected: distanceUnits,
                            unitType: "distance",
                            accessor: `${accessor}.distance_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualDistanceDriven}
                        disabled={true}
                        info={"Assuming only daily commute driving, 5 days a week, 50 weeks a year."}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualEmissions`}
                        label="Annual Emissions"
                        decimals={getDecimalPlaces("emissions")}
                        change={change}
                        units={{
                            base: getBaseUnits("emissions", modelUnits),
                            options: getUnitOptions("emissions"),
                            unitType: "emissions",
                            accessor: `${accessor}.emissions_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualEmissions}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelConsumption`}
                        label="Annual Fuel Consumption"
                        validate={fuelConsumptionValidation}
                        decimals={getDecimalPlaces("fuelConsumption")}
                        change={change}
                        units={{
                            base: getBaseUnits("fuelConsumption", modelUnits),
                            options: getUnitOptions("volume"),
                            unitType: "volume",
                            accessor: `${accessor}.fuelConsumptionUnits_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelConsumption}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelCost`}
                        label="Annual Fuel Cost"
                        decimals={getDecimalPlaces("price")}
                        change={change}
                        units={{
                            base: getBaseUnits("price", modelUnits),
                            options: getUnitOptions("price"),
                            selected: priceUnits,
                            unitType: "price",
                            accessor: `${accessor}.price_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelCost}
                        disabled={true}
                    />
                </InputRow>
            </div>
        </Accordion>
    );
};
